import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Maximize, Minimize, Settings, X, Play, Pause, 
  AlertTriangle, Server, Activity, Clock, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, Minus, Wrench, Target, Timer,
  Volume2, VolumeX, BarChart3, Eye, EyeOff
} from 'lucide-react';
import { cn, formatDuration } from '@/lib/utils';
import { SEVERITY_COLORS, SEVERITY_LABELS } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_PAGES = [
  { id: 'summary', label: 'Resumo', enabled: true },
  { id: 'incidents', label: 'Incidentes', enabled: true },
  { id: 'hosts', label: 'Hosts', enabled: true },
  { id: 'charts', label: 'Gráficos', enabled: true },
];

export function NOCModePage() {
  const [pages, setPages] = useState(DEFAULT_PAGES);
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [rotationInterval, setRotationInterval] = useState(30);
  const [fontSize, setFontSize] = useState(100);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [data, setData] = useState({ 
    realtime: null, 
    incidents: null, 
    hosts: null, 
    trends: null,
    maintenance: null,
    previousIncidents: null
  });
  const [loading, setLoading] = useState(true);
  const [newIncidentsCount, setNewIncidentsCount] = useState(0);
  const [hasNewDisaster, setHasNewDisaster] = useState(false);
  const previousIncidentIds = useRef(new Set());
  const audioRef = useRef(null);
  const { getAuthHeader } = useAuthStore();
  const { refreshInterval } = useThemeStore();
  const navigate = useNavigate();

  const enabledPages = pages.filter(p => p.enabled);

  // NOC Mode settings - same strategy as IncidentsPage to match numbers
  const [includeMaintenance, setIncludeMaintenance] = useState(false); // Exclude hosts in maintenance by default (like Grafana)
  const [showSuppressed, setShowSuppressed] = useState(false); // Hide suppressed by default

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      // Build query params - same strategy as IncidentsPage
      const incidentsParams = new URLSearchParams();
      incidentsParams.set('use_triggers', 'true'); // Use triggers mode like Grafana
      incidentsParams.set('include_maintenance', includeMaintenance.toString());
      incidentsParams.set('include_suppressed', showSuppressed.toString());
      
      const [realtimeRes, incidentsRes, hostsRes, trendsRes, maintenanceRes] = await Promise.all([
        axios.get(`${API}/dashboard/realtime`, { headers: getAuthHeader() }),
        axios.get(`${API}/incidents?${incidentsParams.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/hosts`, { headers: getAuthHeader() }),
        axios.get(`${API}/dashboard/trends?days=7`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
        axios.get(`${API}/maintenance`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
      ]);

      const newIncidents = incidentsRes.data?.incidents || [];
      const currentIds = new Set(newIncidents.map(i => i.id || i.eventid));
      
      // Check for new incidents
      if (previousIncidentIds.current.size > 0) {
        let newCount = 0;
        let hasDisaster = false;
        
        newIncidents.forEach(incident => {
          const incidentId = incident.id || incident.eventid;
          if (!previousIncidentIds.current.has(incidentId)) {
            newCount++;
            if (incident.severity >= 5) {
              hasDisaster = true;
            }
          }
        });
        
        if (newCount > 0) {
          setNewIncidentsCount(prev => prev + newCount);
          if (hasDisaster) {
            setHasNewDisaster(true);
            if (soundEnabled && audioRef.current) {
              audioRef.current.play().catch(() => {});
            }
          }
        }
      }
      
      previousIncidentIds.current = currentIds;

      setData({
        realtime: realtimeRes.data,
        incidents: incidentsRes.data,
        hosts: hostsRes.data,
        trends: trendsRes.data,
        maintenance: maintenanceRes.data
      });
    } catch (err) {
      console.error('Error fetching NOC data:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, soundEnabled, includeMaintenance, showSuppressed]);

  // Auto refresh data
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  // Auto rotate pages
  useEffect(() => {
    if (!isPlaying || enabledPages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % enabledPages.length);
    }, rotationInterval * 1000);
    return () => clearInterval(interval);
  }, [isPlaying, rotationInterval, enabledPages.length]);

  // Fullscreen handling
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Clear new incidents alert
  const clearNewIncidentsAlert = () => {
    setNewIncidentsCount(0);
    setHasNewDisaster(false);
  };

  const goToPrev = () => setCurrentPage(prev => (prev - 1 + enabledPages.length) % enabledPages.length);
  const goToNext = () => setCurrentPage(prev => (prev + 1) % enabledPages.length);

  const togglePage = (pageId) => {
    setPages(prev => prev.map(p => 
      p.id === pageId ? { ...p, enabled: !p.enabled } : p
    ));
  };

  const currentPageData = enabledPages[currentPage];

  return (
    <div 
      className={cn(
        "min-h-screen bg-slate-950 text-white transition-all",
        hasNewDisaster && "animate-disaster-alert"
      )} 
      style={{ fontSize: `${fontSize}%` }}
      data-testid="noc-mode-page"
    >
      {/* Alert Sound */}
      <audio ref={audioRef} src="/sounds/alert.mp3" preload="auto" />
      
      {/* CSS for animations */}
      <style>{`
        @keyframes pulse-critical {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes disaster-alert {
          0%, 100% { background-color: rgb(2 6 23); }
          50% { background-color: rgb(127 29 29 / 0.3); }
        }
        .animate-pulse-critical {
          animation: pulse-critical 1s ease-in-out infinite;
        }
        .animate-disaster-alert {
          animation: disaster-alert 2s ease-in-out 3;
        }
        .severity-disaster { animation: pulse-critical 1s ease-in-out infinite; }
        .severity-high { animation: pulse-critical 2s ease-in-out infinite; }
      `}</style>

      {/* Header */}
      <div className="h-16 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              OneView NOC
              {newIncidentsCount > 0 && (
                <Badge 
                  className="bg-red-600 text-white animate-pulse cursor-pointer"
                  onClick={clearNewIncidentsAlert}
                >
                  +{newIncidentsCount} novos
                </Badge>
              )}
            </h1>
            <p className="text-xs text-slate-400">
              {new Date().toLocaleString('pt-BR')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Page indicators */}
          <div className="flex items-center gap-2 mr-4">
            {enabledPages.map((page, index) => (
              <button
                key={page.id}
                onClick={() => setCurrentPage(index)}
                className={cn(
                  "w-3 h-3 rounded-full transition-all",
                  currentPage === index ? "bg-primary scale-125" : "bg-slate-600 hover:bg-slate-500"
                )}
                title={page.label}
              />
            ))}
          </div>

          <Button variant="ghost" size="icon" onClick={goToPrev} className="text-slate-400 hover:text-white">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={goToNext} className="text-slate-400 hover:text-white">
            <ChevronRight className="h-5 w-5" />
          </Button>

          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setIsPlaying(!isPlaying)}
            className={isPlaying ? 'text-green-400 hover:text-green-300' : 'text-slate-400 hover:text-white'}
          >
            {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
          </Button>

          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={soundEnabled ? 'text-yellow-400 hover:text-yellow-300' : 'text-slate-400 hover:text-white'}
          >
            {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          </Button>

          <Button variant="ghost" size="icon" onClick={() => setSettingsOpen(true)} className="text-slate-400 hover:text-white">
            <Settings className="h-5 w-5" />
          </Button>

          <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="text-slate-400 hover:text-white">
            {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
            <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            {currentPageData?.id === 'summary' && <SummaryView data={data} />}
            {currentPageData?.id === 'incidents' && <IncidentsView data={data.incidents} />}
            {currentPageData?.id === 'hosts' && <HostsView data={data.hosts} />}
            {currentPageData?.id === 'charts' && <ChartsView data={data} />}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 h-12 bg-slate-900 border-t border-slate-800 flex items-center justify-between px-6">
        <span className="text-sm text-slate-400">
          Página {currentPage + 1} de {enabledPages.length}: {currentPageData?.label}
        </span>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-400">
            Atualização: {refreshInterval}s
          </span>
          <span className="text-sm text-slate-400">
            {isPlaying ? `Próxima em ${rotationInterval}s` : 'Pausado'}
          </span>
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Configurações do NOC</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Rotation Interval */}
            <div className="space-y-2">
              <Label>Intervalo de Rotação: {rotationInterval}s</Label>
              <Select value={String(rotationInterval)} onValueChange={(v) => setRotationInterval(Number(v))}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 segundos</SelectItem>
                  <SelectItem value="15">15 segundos</SelectItem>
                  <SelectItem value="30">30 segundos</SelectItem>
                  <SelectItem value="45">45 segundos</SelectItem>
                  <SelectItem value="60">1 minuto</SelectItem>
                  <SelectItem value="120">2 minutos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Font Size */}
            <div className="space-y-2">
              <Label>Tamanho da Fonte: {fontSize}%</Label>
              <Slider
                value={[fontSize]}
                onValueChange={(v) => setFontSize(v[0])}
                min={80}
                max={150}
                step={10}
                className="py-2"
              />
            </div>

            {/* Sound Alert */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Alerta Sonoro</Label>
                <p className="text-xs text-slate-400">Tocar som para incidentes Disaster</p>
              </div>
              <Switch checked={soundEnabled} onCheckedChange={setSoundEnabled} />
            </div>

            {/* Data Filters - Same as IncidentsPage */}
            <div className="space-y-4 border-t border-slate-700 pt-4">
              <Label className="text-slate-300">Filtros de Dados (Igual à página Incidentes)</Label>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Incluir Manutenção</Label>
                  <p className="text-xs text-slate-400">Exibir hosts em manutenção</p>
                </div>
                <Switch checked={includeMaintenance} onCheckedChange={setIncludeMaintenance} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Exibir Suprimidos</Label>
                  <p className="text-xs text-slate-400">Incluir incidentes suprimidos</p>
                </div>
                <Switch checked={showSuppressed} onCheckedChange={setShowSuppressed} />
              </div>
            </div>

            {/* Page Selection */}
            <div className="space-y-2">
              <Label>Páginas Exibidas</Label>
              <div className="space-y-2">
                {pages.map(page => (
                  <div key={page.id} className="flex items-center justify-between py-2">
                    <span>{page.label}</span>
                    <Switch 
                      checked={page.enabled} 
                      onCheckedChange={() => togglePage(page.id)}
                      disabled={pages.filter(p => p.enabled).length <= 1 && page.enabled}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryView({ data }) {
  const { realtime, incidents, hosts, maintenance, trends } = data;

  // Calculate availability (simplified - hosts up / total * 100)
  const totalHosts = hosts?.summary?.total || 0;
  const hostsUp = hosts?.summary?.up || 0;
  const availability = totalHosts > 0 ? ((hostsUp / totalHosts) * 100).toFixed(2) : 0;

  // Calculate MTTR from realtime data
  const mttr = realtime?.avg_resolution_time || incidents?.avg_resolution_time || '-';

  // Hosts in maintenance
  const hostsInMaintenance = maintenance?.hosts_in_maintenance || 0;

  // Trend calculation
  const incidentsTrend = trends?.incidents_trend || [];
  let trendDirection = 'stable';
  if (incidentsTrend.length >= 2) {
    const recent = incidentsTrend[incidentsTrend.length - 1]?.value || 0;
    const previous = incidentsTrend[incidentsTrend.length - 2]?.value || 0;
    if (recent > previous) trendDirection = 'up';
    else if (recent < previous) trendDirection = 'down';
  }

  const criticalCount = (incidents?.by_severity?.disaster || 0) + (incidents?.by_severity?.high || 0);

  return (
    <div className="space-y-6">
      {/* Big Numbers - Row 1 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <MetricCard 
          label="Hosts Monitorados"
          value={totalHosts}
          icon={Server}
          color="blue"
        />
        <MetricCard 
          label="Disponibilidade"
          value={`${availability}%`}
          icon={Target}
          color="green"
          subtitle={`${hostsUp} UP de ${totalHosts}`}
        />
        <MetricCard 
          label="Incidentes Ativos"
          value={incidents?.total || 0}
          icon={AlertTriangle}
          color="yellow"
          trend={trendDirection}
        />
        <MetricCard 
          label="Críticos (Disaster+High)"
          value={criticalCount}
          icon={AlertTriangle}
          color="red"
          pulse={criticalCount > 0}
        />
      </div>

      {/* Big Numbers - Row 2 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <MetricCard 
          label="MTTR"
          value={mttr}
          icon={Timer}
          color="purple"
          subtitle="Tempo médio resolução"
        />
        <MetricCard 
          label="Em Manutenção"
          value={hostsInMaintenance}
          icon={Wrench}
          color="cyan"
          subtitle={`${maintenance?.active || 0} janelas ativas`}
        />
        <MetricCard 
          label="Hosts Warning"
          value={hosts?.summary?.warning || 0}
          icon={Activity}
          color="yellow"
        />
        <MetricCard 
          label="Hosts Down"
          value={hosts?.summary?.down || 0}
          icon={Server}
          color="red"
          pulse={(hosts?.summary?.down || 0) > 0}
        />
      </div>

      {/* Severity Distribution */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
        {[
          { key: 'disaster', label: 'Disaster', severity: 5 },
          { key: 'high', label: 'High', severity: 4 },
          { key: 'average', label: 'Average', severity: 3 },
          { key: 'warning', label: 'Warning', severity: 2 },
          { key: 'information', label: 'Info', severity: 1 },
          { key: 'not_classified', label: 'N/C', severity: 0 }
        ].map(sev => {
          const count = incidents?.by_severity?.[sev.key] || 0;
          const isHighSeverity = sev.severity >= 4 && count > 0;
          
          return (
            <div 
              key={sev.key} 
              className={cn(
                "p-4 rounded-lg text-center transition-all",
                sev.key === 'disaster' && "bg-red-900/50 border border-red-700",
                sev.key === 'high' && "bg-orange-900/50 border border-orange-700",
                sev.key === 'average' && "bg-yellow-900/50 border border-yellow-700",
                sev.key === 'warning' && "bg-blue-900/50 border border-blue-700",
                sev.key === 'information' && "bg-cyan-900/50 border border-cyan-700",
                sev.key === 'not_classified' && "bg-slate-800 border border-slate-700",
                isHighSeverity && `severity-${sev.key}`
              )}
            >
              <p className={cn(
                "text-3xl lg:text-4xl font-bold",
                isHighSeverity && "text-white"
              )}>
                {count}
              </p>
              <p className="text-xs uppercase tracking-wider mt-1 text-slate-300">
                {sev.label}
              </p>
            </div>
          );
        })}
      </div>

      {/* Recent Critical Incidents */}
      <div className="bg-slate-900 rounded-lg p-4">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          Últimos Incidentes Críticos
        </h3>
        <div className="space-y-2">
          {incidents?.incidents
            ?.filter(i => i.severity >= 4)
            .slice(0, 6)
            .map((incident, index) => (
              <div 
                key={incident.id || incident.eventid || index} 
                className={cn(
                  "flex items-center justify-between p-3 rounded bg-slate-800",
                  incident.severity === 5 && "severity-disaster border-l-4 border-red-500",
                  incident.severity === 4 && "severity-high border-l-4 border-orange-500"
                )}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={cn(
                    "w-2 h-10 rounded shrink-0",
                    incident.severity === 5 ? "bg-red-500" : "bg-orange-500"
                  )} />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{incident.name}</p>
                    <p className="text-xs text-slate-400 truncate">{incident.host_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge 
                    className={cn(
                      "text-white",
                      incident.severity === 5 ? "bg-red-600" : "bg-orange-600"
                    )}
                  >
                    {SEVERITY_LABELS[incident.severity]}
                  </Badge>
                  <Badge variant="secondary" className="bg-slate-700">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatDuration(incident.duration) || incident.duration}
                  </Badge>
                </div>
              </div>
            ))}
          {(!incidents?.incidents || incidents.incidents.filter(i => i.severity >= 4).length === 0) && (
            <div className="text-center text-slate-400 py-8">
              <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>Nenhum incidente crítico no momento</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IncidentsView({ data }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-yellow-500" />
          Incidentes Ativos ({data?.total || 0})
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { key: 'disaster', label: 'Disaster', color: 'bg-red-600' },
            { key: 'high', label: 'High', color: 'bg-orange-600' },
            { key: 'average', label: 'Average', color: 'bg-yellow-600' },
            { key: 'warning', label: 'Warning', color: 'bg-blue-600' },
          ].map(sev => {
            const count = data?.by_severity?.[sev.key] || 0;
            return (
              <Badge key={sev.key} className={cn(sev.color, "text-white")}>
                {sev.label}: {count}
              </Badge>
            );
          })}
        </div>
      </div>

      <div className="bg-slate-900 rounded-lg overflow-hidden">
        <div className="max-h-[calc(100vh-16rem)] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-slate-800 sticky top-0">
              <tr>
                <th className="text-left p-4 w-32">Severidade</th>
                <th className="text-left p-4">Problema</th>
                <th className="text-left p-4 w-48">Host</th>
                <th className="text-left p-4 w-32">Duração</th>
                <th className="text-left p-4 w-36">Status</th>
              </tr>
            </thead>
            <tbody>
              {data?.incidents?.map((incident, index) => {
                const isHighSeverity = incident.severity >= 4;
                return (
                  <tr 
                    key={incident.id || incident.eventid || index} 
                    className={cn(
                      "border-t border-slate-800 hover:bg-slate-800/50",
                      isHighSeverity && `severity-${incident.severity === 5 ? 'disaster' : 'high'}`
                    )}
                  >
                    <td className="p-4">
                      <Badge 
                        style={{ backgroundColor: SEVERITY_COLORS[incident.severity] }}
                        className="text-white"
                      >
                        {SEVERITY_LABELS[incident.severity] || 'N/C'}
                      </Badge>
                    </td>
                    <td className="p-4 max-w-md">
                      <p className="truncate">{incident.name}</p>
                    </td>
                    <td className="p-4 text-slate-300">{incident.host_name || '-'}</td>
                    <td className="p-4 text-slate-300 whitespace-nowrap">
                      {formatDuration(incident.duration) || incident.duration}
                    </td>
                    <td className="p-4">
                      {incident.acknowledged ? (
                        <Badge className="bg-green-600/20 text-green-400 border border-green-600/30">
                          ✓ ACK
                        </Badge>
                      ) : (
                        <Badge className="bg-red-600/20 text-red-400 border border-red-600/30">
                          Não ACK
                        </Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(!data?.incidents || data.incidents.length === 0) && (
            <div className="text-center text-slate-400 py-16">
              <AlertTriangle className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-xl">Nenhum incidente ativo</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HostsView({ data }) {
  // Sort hosts: down first, then warning, then up
  const sortedHosts = [...(data?.hosts || [])].sort((a, b) => {
    const statusOrder = { down: 0, warning: 1, up: 2 };
    return (statusOrder[a.status] || 3) - (statusOrder[b.status] || 3);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Server className="h-6 w-6 text-blue-500" />
          Hosts ({data?.summary?.total || 0})
        </h2>
        <div className="flex items-center gap-4">
          <Badge className="bg-green-600/20 text-green-400 border border-green-600/30 text-lg px-4 py-2">
            <div className="w-3 h-3 rounded-full bg-green-500 mr-2" />
            UP: {data?.summary?.up || 0}
          </Badge>
          <Badge className="bg-yellow-600/20 text-yellow-400 border border-yellow-600/30 text-lg px-4 py-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2" />
            Warning: {data?.summary?.warning || 0}
          </Badge>
          <Badge className={cn(
            "bg-red-600/20 text-red-400 border border-red-600/30 text-lg px-4 py-2",
            (data?.summary?.down || 0) > 0 && "animate-pulse"
          )}>
            <div className="w-3 h-3 rounded-full bg-red-500 mr-2" />
            Down: {data?.summary?.down || 0}
          </Badge>
        </div>
      </div>

      <div className="bg-slate-900 rounded-lg overflow-hidden">
        <div className="max-h-[calc(100vh-16rem)] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-slate-800 sticky top-0">
              <tr>
                <th className="text-left p-4 w-16">Status</th>
                <th className="text-left p-4">Host</th>
                <th className="text-left p-4 w-48">Grupo</th>
                <th className="text-left p-4 w-36">IP</th>
                <th className="text-left p-4">Problema Atual</th>
              </tr>
            </thead>
            <tbody>
              {sortedHosts.map((host, index) => (
                <tr 
                  key={host.hostid || index} 
                  className={cn(
                    "border-t border-slate-800 hover:bg-slate-800/50",
                    host.status === 'down' && "bg-red-900/20"
                  )}
                >
                  <td className="p-4">
                    <div className={cn(
                      "w-4 h-4 rounded-full",
                      host.status === 'up' && "bg-green-500",
                      host.status === 'warning' && "bg-yellow-500",
                      host.status === 'down' && "bg-red-500 animate-pulse"
                    )} />
                  </td>
                  <td className="p-4 font-medium">{host.name}</td>
                  <td className="p-4 text-slate-300">{host.category || host.groups?.[0]?.name || '-'}</td>
                  <td className="p-4 text-slate-400 font-mono text-sm">{host.ip || '-'}</td>
                  <td className="p-4 text-slate-300 max-w-sm truncate">
                    {host.current_trigger || host.triggers?.[0]?.description || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortedHosts.length === 0 && (
            <div className="text-center text-slate-400 py-16">
              <Server className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-xl">Nenhum host encontrado</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChartsView({ data }) {
  const { trends, incidents, realtime } = data;
  
  // Incidents by day data
  const incidentsTrend = trends?.incidents_trend || [];
  const maxIncidents = Math.max(...incidentsTrend.map(d => d.value), 1);
  
  // MTTR trend
  const mttrTrend = trends?.mttr_trend || [];
  const maxMttr = Math.max(...mttrTrend.map(d => d.value), 1);
  
  // Top hosts by problems (from realtime)
  const topHosts = realtime?.top_hosts || [];
  const maxHostProblems = Math.max(...topHosts.map(h => h.count), 1);

  // Severity distribution for pie chart simulation
  const severityData = [
    { label: 'Disaster', value: incidents?.by_severity?.disaster || 0, color: '#DC2626' },
    { label: 'High', value: incidents?.by_severity?.high || 0, color: '#EA580C' },
    { label: 'Average', value: incidents?.by_severity?.average || 0, color: '#CA8A04' },
    { label: 'Warning', value: incidents?.by_severity?.warning || 0, color: '#2563EB' },
    { label: 'Info', value: incidents?.by_severity?.information || 0, color: '#0891B2' },
  ].filter(d => d.value > 0);
  const totalSeverity = severityData.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <BarChart3 className="h-6 w-6 text-purple-500" />
        Análise de Tendências
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Incidents Trend Chart */}
        <div className="bg-slate-900 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-red-400" />
            Incidentes (Últimos 7 dias)
          </h3>
          <div className="h-48">
            {incidentsTrend.length > 0 ? (
              <div className="flex items-end justify-between h-full gap-2">
                {incidentsTrend.map((day, index) => {
                  const height = (day.value / maxIncidents) * 100;
                  return (
                    <div key={day.date || day.label || index} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-slate-400">{day.value}</span>
                      <div 
                        className="w-full bg-gradient-to-t from-red-600 to-red-400 rounded-t transition-all"
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                      <span className="text-xs text-slate-500">{day.label}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                Dados insuficientes
              </div>
            )}
          </div>
        </div>

        {/* MTTR Trend Chart */}
        <div className="bg-slate-900 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Timer className="h-5 w-5 text-blue-400" />
            Tempo Médio de Resolução (min)
          </h3>
          <div className="h-48">
            {mttrTrend.length > 0 ? (
              <div className="flex items-end justify-between h-full gap-2">
                {mttrTrend.map((day, index) => {
                  const height = maxMttr > 0 ? (day.value / maxMttr) * 100 : 0;
                  return (
                    <div key={day.date || day.label || index} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-slate-400">{day.value}</span>
                      <div 
                        className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all"
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                      <span className="text-xs text-slate-500">{day.label}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                Dados insuficientes
              </div>
            )}
          </div>
        </div>

        {/* Severity Distribution */}
        <div className="bg-slate-900 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            Distribuição por Severidade
          </h3>
          <div className="space-y-3">
            {severityData.length > 0 ? (
              severityData.map((sev, index) => {
                const percentage = totalSeverity > 0 ? (sev.value / totalSeverity) * 100 : 0;
                return (
                  <div key={sev.label || index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{sev.label}</span>
                      <span className="text-slate-400">{sev.value} ({percentage.toFixed(0)}%)</span>
                    </div>
                    <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ width: `${percentage}%`, backgroundColor: sev.color }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center justify-center h-32 text-slate-400">
                Nenhum incidente ativo
              </div>
            )}
          </div>
        </div>

        {/* Top Hosts */}
        <div className="bg-slate-900 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Server className="h-5 w-5 text-purple-400" />
            Top 5 Hosts com Mais Problemas
          </h3>
          <div className="space-y-3">
            {topHosts.length > 0 ? (
              topHosts.slice(0, 5).map((host, index) => {
                const percentage = (host.count / maxHostProblems) * 100;
                return (
                  <div key={host.hostid || host.name || index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="truncate flex-1">{host.name}</span>
                      <span className="text-slate-400 ml-2">{host.count} eventos</span>
                    </div>
                    <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-600 to-purple-400 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center justify-center h-32 text-slate-400">
                Dados insuficientes
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, color, subtitle, trend, pulse }) {
  const colorClasses = {
    blue: 'bg-blue-900/30 border-blue-700 text-blue-400',
    green: 'bg-green-900/30 border-green-700 text-green-400',
    yellow: 'bg-yellow-900/30 border-yellow-700 text-yellow-400',
    red: 'bg-red-900/30 border-red-700 text-red-400',
    purple: 'bg-purple-900/30 border-purple-700 text-purple-400',
    cyan: 'bg-cyan-900/30 border-cyan-700 text-cyan-400',
  };

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' ? 'text-red-400' : trend === 'down' ? 'text-green-400' : 'text-slate-400';

  return (
    <div className={cn(
      "p-4 lg:p-6 rounded-lg border transition-all",
      colorClasses[color],
      pulse && "animate-pulse"
    )}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-slate-400">{label}</p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-3xl lg:text-5xl font-bold">{value}</p>
            {trend && (
              <TrendIcon className={cn("h-5 w-5", trendColor)} />
            )}
          </div>
          {subtitle && (
            <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
          )}
        </div>
        <Icon className="h-8 w-8 lg:h-12 lg:w-12 opacity-50" />
      </div>
    </div>
  );
}
